import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  FaCamera,
  FaCheckCircle,
  FaEnvelope,
  FaIdBadge,
  FaLock,
  FaPen,
  FaSignOutAlt,
  FaUser,
} from 'react-icons/fa';
import { Placeholder } from '../components/Placeholder';
import { fetchMyProfile } from '../redux/lounges/slice';
import { selectLounges } from '../redux/lounges/selectors';
import { useAppDispatch } from '../redux/store';
import { verifyEmail } from '../redux/users/slice';
import { GET_BASE_URL_IMAGE, dTime } from '../constants/apiEndpoints';

type FormData = {
  email: string;
  uname: string;
  user_description: string;
  isdirect: string;
  myfile: string;
};

type SettingFieldProps = {
  icon: ReactNode;
  label: string;
  value: string | null;
  to: string;
  actionLabel: string;
  multiline?: boolean;
};

const SettingField = ({
  icon,
  label,
  value,
  to,
  actionLabel,
  multiline = false,
}: SettingFieldProps) => (
  <div className="mw-settings-field">
    <div className="mw-settings-field__icon" aria-hidden="true">
      {icon}
    </div>
    <div className="mw-settings-field__body">
      <span className="mw-settings-field__label">{label}</span>
      {multiline ? (
        <p className="mw-settings-field__value mw-settings-field__value--multiline">
          {value || 'Not set'}
        </p>
      ) : (
        <span className="mw-settings-field__value">{value || 'Not set'}</span>
      )}
    </div>
    <Link className="mw-settings-action" to={to}>
      <FaPen aria-hidden="true" />
      <span>{actionLabel}</span>
    </Link>
  </div>
);

const Setting = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getProfileItem, status } = useSelector(selectLounges);
  const { register, setValue } = useForm<FormData>();

  const [uname, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isdirect, setIsDirect] = useState('');
  const [image, setImage] = useState('');
  const [description, setDescription] = useState('');
  const [verified, setVerified] = useState('');

  useEffect(() => {
    const key: null = null;
    dispatch<any>(fetchMyProfile({ key })).then((res: any) => {
      const user = res.payload?.[0]?.user;
      if (!user) return;

      setName(user.user_name || '');
      setEmail(user.user_email || '');
      setDescription(user.user_description || '');
      setIsDirect(user.isdirect_msg || '');
      setImage(user.image || '');
      setVerified(user.isvarified || '');
    });
  }, [dispatch]);

  useEffect(() => {
    setValue('uname', uname);
    setValue('email', email);
    setValue('isdirect', isdirect);
    setValue('myfile', image);
    setValue('user_description', description);
  }, [description, email, image, isdirect, setValue, uname]);

  const onLogOut = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_name');
    localStorage.removeItem('email');
    localStorage.clear();
    navigate('/disneyland/lounge/');
    window.location.reload();
  };

  const verifyMail = () => {
    dispatch<any>(verifyEmail({ verifymail: 't' })).then((res: any) => {
      window.confirm(res.payload.data);
    });
  };

  const onVerifyClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (
      window.confirm(
        "We just sent an email to you, if you don't get it please check your spam folder and make sure info@mousewait.com and support@mousewait.com is in your contacts. Click on the activation link in that email and your account will be verified (also make sure your email is correct in the box to the left). Refresh the Lounge and then you will be able to post and comment, have fun!"
      )
    ) {
      verifyMail();
    }
  };

  const profileImageSrc = image
    ? `${GET_BASE_URL_IMAGE}/disneyland/images/userimg/${image}${dTime}`
    : '';
  const hasError = getProfileItem?.some((item: any) => item.error != null);
  const errorMessage = getProfileItem?.find((item: any) => item.error != null)?.error;

  return (
    <div className="mid-main mw-settings-page">
      <div className="container">
        <div className="mid-sec">
          {status === 'loading' ? (
            [...new Array(5)].map((_, index) => <Placeholder key={index} />)
          ) : hasError ? (
            <div className="mw-settings-shell">
              <div className="mw-settings-panel">
                <p className="mw-settings-error">{errorMessage}</p>
              </div>
            </div>
          ) : (
            <div className="mw-settings-shell">
              <header className="mw-settings-hero">
                <div>
                  <span className="mw-settings-kicker">MouseWait Account</span>
                  <h1>Settings</h1>
                  <p>Manage your profile, email, password, and account access.</p>
                </div>
                <Link className="mw-settings-hero__back" to="/disneyland/lounge/">
                  Back to Lounge
                </Link>
              </header>

              <section className="mw-settings-grid">
                <aside className="mw-settings-profile-card">
                  <div className="mw-settings-avatar-wrap">
                    {profileImageSrc ? (
                      <img
                        className="mw-settings-avatar"
                        src={profileImageSrc}
                        alt={uname || 'Profile'}
                      />
                    ) : (
                      <div className="mw-settings-avatar mw-settings-avatar--empty">
                        <FaUser aria-hidden="true" />
                      </div>
                    )}
                  </div>
                  <h2>{uname || 'MouseWaiter'}</h2>
                  <p>{email || 'No email on file'}</p>
                  <Link className="mw-settings-primary-action" to="/disneyland/setting/dp">
                    <FaCamera aria-hidden="true" />
                    <span>Update Photo</span>
                  </Link>
                </aside>

                <section className="mw-settings-panel">
                  <div className="mw-settings-panel__header">
                    <div>
                      <span className="mw-settings-kicker">Profile</span>
                      <h2>Account Details</h2>
                    </div>
                    <span
                      className={`mw-settings-verify ${
                        verified === '0' ? 'mw-settings-verify--pending' : ''
                      }`}
                    >
                      <FaCheckCircle aria-hidden="true" />
                      {verified === '0' ? 'Verification needed' : 'Verified'}
                    </span>
                  </div>

                  <form className="mw-settings-form">
                    <input type="hidden" {...register('uname')} />
                    <input type="hidden" {...register('email')} />
                    <input type="hidden" {...register('isdirect')} />
                    <input type="hidden" {...register('myfile')} />
                    <input type="hidden" {...register('user_description')} />

                    <SettingField
                      icon={<FaIdBadge />}
                      label="Name"
                      value={uname}
                      to="/disneyland/setting/name"
                      actionLabel="Edit"
                    />
                    <SettingField
                      icon={<FaEnvelope />}
                      label="Email"
                      value={email}
                      to="/disneyland/setting/email"
                      actionLabel="Edit"
                    />
                    <SettingField
                      icon={<FaUser />}
                      label="About"
                      value={description}
                      to="/disneyland/setting/about"
                      actionLabel="Edit"
                      multiline
                    />
                  </form>

                  <div className="mw-settings-footer-actions">
                    <Link className="mw-settings-secondary-action" to="/disneyland/changepassword">
                      <FaLock aria-hidden="true" />
                      <span>Change Password</span>
                    </Link>
                    {verified === '0' ? (
                      <button
                        className="mw-settings-secondary-action"
                        type="button"
                        onClick={onVerifyClick}
                      >
                        <FaEnvelope aria-hidden="true" />
                        <span>Verify Email</span>
                      </button>
                    ) : (
                      <span className="mw-settings-secondary-action mw-settings-secondary-action--static">
                        <FaCheckCircle aria-hidden="true" />
                        <span>Email verified</span>
                      </span>
                    )}
                    <button
                      className="mw-settings-secondary-action mw-settings-secondary-action--danger"
                      type="button"
                      onClick={onLogOut}
                    >
                      <FaSignOutAlt aria-hidden="true" />
                      <span>Logout</span>
                    </button>
                  </div>
                </section>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Setting;
